import at from 'lodash/at';
import find from 'lodash/find';
import get from 'lodash/get';
import { useMemo } from 'react';
import {
  ColDef,
  CellClassParams,
  ValueGetterParams,
  HeaderClassParams,
  HeaderValueGetterParams,
  GetMainMenuItemsParams,
  MenuItemDef,
} from '@ag-grid-community/core';

import {
  getAgTableHeaderName,
  getCustomCellStyle,
  getCustomHeaderStyle,
} from '../helpers/TableColumnFormatting';
import { getFieldName } from '../helpers/getFieldName';
import { GroupAndTotalRenderer } from '../GroupAndTotalRenderer';
import { I18n } from 'common/i18n';
import { NULL_GROUPING } from '../Constants';
import { CustomAgGridContext } from 'common/types/agGrid/context';
import { generateCellClasses } from 'common/visualizations/views/agGridReact/helpers/TableExportFormatting';
import getCustomSortMenuItems from '../helpers/getCustomSortMenuItems';
import { ViewColumn } from 'common/types/viewColumn';
import {
  CustomLabels,
  CustomSortConfig,
  Hierarchy,
  HierarchyColumnConfig,
  OrderConfig,
} from 'common/visualizations/vif';
import { UniqueValuesData, TableColumnFormat, MAX_CUSTOM_SORT_UNIQUE_ROW_COUNT, FormatStyle } from '../types';
import { FeatureFlags } from '@socrata/platform/feature-flags';
import { tooltipValueGetterFunction } from '../helpers/ColDefHelpers';
import { isTotalCell, isSubtotalCell } from '../helpers/isTotalCell';
import { isTableAdvancedTotalingEnabled } from 'common/visualizations/helpers/VifSelectors';
import isUndefined from 'lodash/isUndefined';
import isBlank from 'common/js_utils/isBlank';

const scope = 'shared.visualizations.charts';

const noValueText = I18n.t('common.summary_table.no_value', { scope });
const totalText = I18n.t('table.total', { scope });
const netTotalText = I18n.t('table.net_total', { scope });

export interface AutoGroupAttributesProps {
  datasetUid: string;
  domain: string;
  isIndented?: boolean;
  singleAutoColumnWidth?: number;
  shouldGenerateCellClasses?: boolean;
  showColumnMenuKebab?: boolean;
  mainMenuItems?: (string | MenuItemDef)[];
  columns?: ViewColumn[];
  setIsCustomSortModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedColumns?: ViewColumn[];
  setSelectedColumns?: React.Dispatch<React.SetStateAction<ViewColumn[]>>;
  hierarchies?: HierarchyColumnConfig[];
  getUniqueValues?: (
    columns: ViewColumn[],
    order: OrderConfig[],
    customSortConfig: CustomSortConfig | undefined,
  ) => Promise<UniqueValuesData>;
  vifOrderConfig: OrderConfig[];
  columnFormats: { [key: string]: TableColumnFormat };
  hierarchyConfig: Hierarchy | undefined;
  distinctValuesCountMap?: object;
  setCurrentSortState?: React.Dispatch<React.SetStateAction<OrderConfig[]>>;
  handleCustomSortCycle?: (newOrderConfig: OrderConfig[], hierarchyId?: string) => void;
  handleIndentedHeaderSortIndex?: (headerSortCycleIndex: number, hierarchyId?: string) => void;
  headerFormat: FormatStyle;
}

const getHeaderValue = (params: HeaderValueGetterParams, isIndented: boolean | undefined) => {
  const { columnMetadata, columnFormats } = params.context as CustomAgGridContext;
  let fieldName = '';
  if (isIndented) {
    fieldName = params?.api.getRowGroupColumns()[0].getColDef().field ?? '';
  } else {
    // params.colDef can be a single column or a row group, but the type doesn't include specific fields from either
    // @ts-expect-error TS(2339) FIXME: Property 'field' does not exist on type 'AbstractC... Remove this comment to see the full error message
    fieldName = params?.colDef?.field || params?.colDef?.showRowGroup;
  }

  const localColumnMetadata = columnMetadata.find((c) => c.fieldName === fieldName);
  if (!localColumnMetadata) {
    return params.colDef.headerName;
  }
  return getAgTableHeaderName(columnFormats[fieldName], localColumnMetadata);
};

const getHeaderClass = (params: HeaderClassParams, isIndented: boolean | undefined) => {
  const { columnMetadata, columnFormats } = params.context as CustomAgGridContext;
  let fieldName = '';
  if (isIndented) {
    fieldName = get(params, 'colDef.field', '');
  } else {
    // params.colDef can be a single column or a row group, but the type doesn't include specific fields from either
    // @ts-expect-error TS(2339) FIXME: Property 'field' does not exist on type 'AbstractC... Remove this comment to see the full error message
    fieldName = params?.colDef?.field || params?.colDef?.showRowGroup;
  }
  const localColumnMetadata = columnMetadata.find((c) => c.fieldName === fieldName);
  if (!localColumnMetadata) {
    return '';
  }
  const customHeaderStyle = getCustomHeaderStyle(columnFormats[fieldName]);

  return [customHeaderStyle, 'ag-grid-header'];
};

const getCellStyle = (params: CellClassParams) => {
  const { showSubTotal, nonStandardAggregations, columnMetadata, columnFormats, currentRowStripeStyle } =
    params.context as CustomAgGridContext;
  const fieldName = getFieldName(params);
  const localColumnMetadata = columnMetadata.find((c) => c.fieldName === fieldName);
  if (!localColumnMetadata || !fieldName) {
    return {};
  }
  const customCellStyle = getCustomCellStyle({
    column: localColumnMetadata,
    nonStandardAggregations: nonStandardAggregations,
    columnFormat: columnFormats[fieldName],
    params,
    showSubTotal,
    currentRowStripeStyle,
  });
  return customCellStyle;
};

const getValue = (params: ValueGetterParams, isIndented?: boolean, customLabels?: CustomLabels) => {
  // If we have a total row we want the first grouped column to have "Total" as the value
  const columnDefs: ColDef[] | undefined = params?.api?.getColumnDefs();
  if (!columnDefs) {
    return;
  }

  const { showNetTotal } = params.context as CustomAgGridContext;
  let totalReturnText = totalText;

  if (isTableAdvancedTotalingEnabled()) {
    const customLabel = customLabels?.totals?.trim();
    if (!isUndefined(customLabels?.totals) && !isBlank(customLabel)) {
      totalReturnText = customLabel;
    }
  } else if (FeatureFlags.value('enable_prototype_net_total') && showNetTotal) {
    totalReturnText = netTotalText;
  }

  const fieldName = getFieldName(params);
  if (isTotalCell(params)) {
    if (isIndented) {
      return totalReturnText;
    }
    const firstGroupedColumn = find(columnDefs, ['rowGroupIndex', 0]);
    const firstGroupedColumnField = firstGroupedColumn?.field;
    if (fieldName === firstGroupedColumnField) {
      return totalReturnText;
    }
  }

  const nodeField = params.node?.field;
  if (params.node?.footer && fieldName && fieldName === nodeField) {
    const value = params.getValue(fieldName);
    return value == NULL_GROUPING ? noValueText : value;
  }
};

const getCellClass = (params: CellClassParams) => {
  const fieldName = getFieldName(params) ?? '';
  const column = find(params.context.columnMetadata, { fieldName: fieldName });
  // params.node.key contains the true/unformatted value
  // whereas params.value contains the formatted value from the renderer (exportHooks - processCellCallback).
  const dataValue = params.node.key;
  const value = dataValue || params.value;
  const cellClassesParams = {
    colFieldName: fieldName,
    colDataTypeName: column?.dataTypeName ?? '',
    node: params.node,
    context: params.context,
    value: value,
    colDef: params.colDef,
  };

  return generateCellClasses(cellClassesParams);
};

const getCellClassRules = (params: CellClassParams, rule: string) => {
  const colId = params.colDef?.field;
  if (!colId) {
    return false;
  }
  return params.context.columnFormats[colId]?.format.align == rule;
};

export const useAutoGroupAttributes = ({
  columnFormats,
  columns,
  datasetUid,
  distinctValuesCountMap,
  domain,
  getUniqueValues,
  handleCustomSortCycle,
  handleIndentedHeaderSortIndex,
  headerFormat,
  hierarchies,
  hierarchyConfig,
  isIndented,
  mainMenuItems = [],
  selectedColumns,
  setCurrentSortState,
  setIsCustomSortModalOpen,
  setSelectedColumns,
  shouldGenerateCellClasses,
  showColumnMenuKebab,
  singleAutoColumnWidth,
  vifOrderConfig,
}: AutoGroupAttributesProps) => {
  return useMemo<ColDef>(() => {
    const getMainMenuItems = (params: GetMainMenuItemsParams) => {
      let isColumnDisabled: boolean;

      if (isIndented) {
        isColumnDisabled = at(distinctValuesCountMap, params.context.groupedColumns).every(
          (value: string) => parseInt(value) > MAX_CUSTOM_SORT_UNIQUE_ROW_COUNT,
        );
      } else {
        const fieldNameParams = {
          column: params.column,
          colDef: params.column.getColDef(),
          value: undefined,
        } as CellClassParams;
        const colId = isIndented ? params.context.groupedColumns[0] : getFieldName(fieldNameParams);
        const columnMap = get(distinctValuesCountMap, colId, '');
        isColumnDisabled = parseInt(columnMap) > MAX_CUSTOM_SORT_UNIQUE_ROW_COUNT;
      }

      return getCustomSortMenuItems({
        getMainMenuParams: params,
        mainMenuItems,
        columns,
        setIsCustomSortModalOpen,
        setSelectedColumns,
        selectedColumns,
        hierarchies,
        getUniqueValues,
        vifOrderConfig,
        columnFormats,
        hierarchyId: hierarchyConfig?.id,
        customSortConfig: hierarchyConfig?.columnSortConfig,
        isColumnDisabled,
        distinctValuesCountMap,
        setCurrentSortState,
        handleCustomSortCycle,
        handleIndentedHeaderSortIndex,
      });
    };
    const width = isIndented ? singleAutoColumnWidth : undefined;
    const wrapHeaders = headerFormat?.wrapHeaders ?? false;
    return {
      headerTooltip: 'Group',
      headerValueGetter: (params: HeaderValueGetterParams) => {
        return getHeaderValue(params, isIndented);
      },
      tooltipComponentParams: {
        domain: domain,
        datasetUid: datasetUid,
        isIndented: isIndented,
      },
      headerClass: (params: HeaderClassParams) => {
        return getHeaderClass(params, isIndented);
      },
      cellStyle: getCellStyle,
      tooltipValueGetter: tooltipValueGetterFunction,
      cellRendererParams: {
        innerRenderer: GroupAndTotalRenderer,
        innerRendererParams: {
          datasetUid,
          domain,
          wrapWithDivElement: false,
          customLabels: hierarchyConfig?.customLabels,
        },
      },
      valueGetter: (params: ValueGetterParams) => {
        return getValue(params, isIndented, hierarchyConfig?.customLabels);
      },
      width,
      mainMenuItems: showColumnMenuKebab ? getMainMenuItems : undefined,
      ...(shouldGenerateCellClasses
        ? {
            cellClass: getCellClass,
            cellClassRules: {
              'left-align': (params) => getCellClassRules(params, 'left'),
              'center-align': (params) => getCellClassRules(params, 'center'),
              'right-align': (params) => getCellClassRules(params, 'right'),
              'total-row': isTotalCell,
              'subtotal-row': isSubtotalCell,
            },
          }
        : {}),
      wrapHeaderText: wrapHeaders,
      autoHeaderHeight: wrapHeaders,
    } as ColDef;
  }, [
    datasetUid,
    domain,
    headerFormat,
    isIndented,
    singleAutoColumnWidth,
    hierarchies,
    columns,
    vifOrderConfig,
    columnFormats,
    hierarchyConfig,
    distinctValuesCountMap,
  ]);
};
